/* ============================================================
   MODERN ALCHEMY — theme.js
   Global UI interactions, cart, drawer, accordion, sticky ATC
   ============================================================ */

'use strict';

/* ── UTILITIES ── */
const MA = {
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  },
  qs(selector, scope = document) {
    return scope.querySelector(selector);
  },
  qsa(selector, scope = document) {
    return [...scope.querySelectorAll(selector)];
  },
  on(el, event, handler) {
    if (el) el.addEventListener(event, handler);
  },
  emit(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
  }
};

/* ─────────────────────────────────────────────
   HEADER & NAV
───────────────────────────────────────────── */
class SiteHeader {
  constructor() {
    this.header = MA.qs('.site-header');
    this.navDrawer = MA.qs('.nav-drawer');
    this.navOverlay = MA.qs('.nav-drawer__overlay');
    this.menuBtn = MA.qs('.header__menu-btn');
    this.closeBtn = MA.qs('.nav-drawer__close');
    this.cartBtn = MA.qs('.header__cart-btn');
    this.cartDrawer = MA.qs('.cart-drawer');
    this.cartOverlay = MA.qs('.cart-drawer__overlay');
    this.cartClose = MA.qs('.cart-drawer__close');
    this.lastScroll = 0;
    this.init();
  }

  init() {
    MA.on(this.menuBtn, 'click', () => this.openNav());
    MA.on(this.closeBtn, 'click', () => this.closeNav());
    MA.on(this.navOverlay, 'click', () => this.closeNav());
    MA.on(this.cartBtn, 'click', () => this.openCart());
    MA.on(this.cartClose, 'click', () => this.closeCart());
    MA.on(this.cartOverlay, 'click', () => this.closeCart());

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        this.closeNav();
        this.closeCart();
      }
    });

    // Scroll-based header behaviour
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.updateCartCount());
  }

  openNav() {
    this.navDrawer?.classList.add('is-open');
    this.navOverlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.menuBtn?.setAttribute('aria-expanded', 'true');
  }

  closeNav() {
    this.navDrawer?.classList.remove('is-open');
    this.navOverlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    this.menuBtn?.setAttribute('aria-expanded', 'false');
  }

  openCart() {
    this.cartDrawer?.classList.add('is-open');
    this.cartOverlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    MA.emit('cart:open');
  }

  closeCart() {
    this.cartDrawer?.classList.remove('is-open');
    this.cartOverlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  handleScroll() {
    const current = window.scrollY;
    if (this.header) {
      this.header.classList.toggle('is-scrolled', current > 20);
    }
    this.lastScroll = current;
  }

  async updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const badges = MA.qsa('.cart-count');
      badges.forEach(b => {
        b.textContent = cart.item_count;
        b.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    } catch (e) { console.error('Cart count update failed', e); }
  }
}

/* ─────────────────────────────────────────────
   ACCORDION
───────────────────────────────────────────── */
class Accordion {
  constructor(el) {
    this.el = el;
    this.triggers = MA.qsa('.accordion__trigger', el);
    this.init();
  }

  init() {
    this.triggers.forEach(trigger => {
      MA.on(trigger, 'click', () => this.toggle(trigger));
    });
  }

  toggle(trigger) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    // Close all
    this.triggers.forEach(t => {
      t.setAttribute('aria-expanded', 'false');
      const body = MA.qs('.accordion__content', t.closest('.accordion__item'));
      body?.classList.remove('is-open');
    });
    // Open clicked if it was closed
    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      const body = MA.qs('.accordion__content', trigger.closest('.accordion__item'));
      body?.classList.add('is-open');
    }
  }
}

/* ─────────────────────────────────────────────
   FILTER PILLS
───────────────────────────────────────────── */
class FilterPills {
  constructor(el) {
    this.el = el;
    this.pills = MA.qsa('.filter-pill', el);
    this.init();
  }

  init() {
    this.pills.forEach(pill => {
      MA.on(pill, 'click', () => {
        this.pills.forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        MA.emit('filter:changed', { value: pill.dataset.filter });
      });
    });
  }
}

/* ─────────────────────────────────────────────
   QUANTITY CONTROLS
───────────────────────────────────────────── */
class QuantityControl {
  constructor(el) {
    this.el = el;
    this.input = MA.qs('.qty-ctrl__val', el) || MA.qs('[data-qty-val]', el);
    this.decBtn = MA.qs('[data-qty-dec]', el);
    this.incBtn = MA.qs('[data-qty-inc]', el);
    this.min = parseInt(el.dataset.min || '1');
    this.max = parseInt(el.dataset.max || '999');
    this.init();
  }

  get value() { return parseInt(this.input?.textContent || '1'); }
  set value(v) { if (this.input) this.input.textContent = Math.min(this.max, Math.max(this.min, v)); }

  init() {
    MA.on(this.decBtn, 'click', () => {
      this.value = this.value - 1;
      MA.emit('qty:changed', { value: this.value, el: this.el });
    });
    MA.on(this.incBtn, 'click', () => {
      this.value = this.value + 1;
      MA.emit('qty:changed', { value: this.value, el: this.el });
    });
  }
}

/* ─────────────────────────────────────────────
   ADD TO CART
───────────────────────────────────────────── */
class AddToCart {
  constructor(form) {
    this.form = form;
    this.btn = MA.qs('[data-atc-btn]', form);
    this.init();
  }

  init() {
    MA.on(this.form, 'submit', e => {
      e.preventDefault();
      this.submit();
    });
  }

  async submit() {
    if (!this.btn) return;
    const originalText = this.btn.textContent;
    this.btn.textContent = 'Adding...';
    this.btn.disabled = true;

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const qty = parseInt(formData.get('quantity') || '1');

    // Check for ritual note
    const ritualNote = MA.qs('[data-ritual-note]', this.form)?.value || '';

    try {
      const body = {
        items: [{
          id: variantId,
          quantity: qty,
          properties: ritualNote ? { 'Ritual Intention': ritualNote } : {}
        }]
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Add to cart failed');

      this.btn.textContent = 'Added ✓';
      this.btn.style.background = 'var(--sage)';
      MA.emit('cart:updated');

      // Open cart drawer
      setTimeout(() => {
        document.querySelector('.site-header')?.__header?.openCart();
        MA.qs('.cart-drawer')?.classList.add('is-open');
        MA.qs('.cart-drawer__overlay')?.classList.add('is-open');
      }, 400);

      // Reset button
      setTimeout(() => {
        this.btn.textContent = originalText;
        this.btn.style.background = '';
        this.btn.disabled = false;
      }, 2500);

    } catch (err) {
      console.error(err);
      this.btn.textContent = 'Try again';
      this.btn.disabled = false;
      setTimeout(() => { this.btn.textContent = originalText; }, 2000);
    }
  }
}

/* ─────────────────────────────────────────────
   CART DRAWER — Live render
───────────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.drawer = MA.qs('.cart-drawer');
    this.body = MA.qs('.cart-drawer__body');
    this.totalEl = MA.qs('.cart-drawer__total');
    document.addEventListener('cart:updated', () => this.render());
    document.addEventListener('cart:open', () => this.render());
  }

  async render() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (this.body) this.body.innerHTML = this.buildItems(cart);
      if (this.totalEl) this.totalEl.textContent = MA.formatMoney(cart.total_price);
      this.bindEvents();
    } catch (e) { console.error('Cart render failed', e); }
  }

  buildItems(cart) {
    if (!cart.items.length) {
      return `<div style="text-align:center;padding:48px 0;">
        <div style="font-family:var(--fd);font-size:22px;font-weight:300;color:var(--mist);margin-bottom:12px;">Your cart is empty</div>
        <a href="/collections/all" style="font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--gold);">Begin Shopping →</a>
      </div>`;
    }

    return cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <img src="${item.image}" alt="${item.title}" width="72" height="90" loading="lazy">
          ${item.vendor === 'Sol Alchemy' ? '<span class="badge badge-sa" style="position:absolute;top:0;left:0;right:0;text-align:center;">Sol Alchemy</span>' : ''}
        </div>
        <div>
          <div class="cart-item__brand">${item.vendor} · ${item.product_type}</div>
          <div class="cart-item__name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__meta">${item.variant_title}</div>` : ''}
          ${item.properties?.['Ritual Intention'] ? `<div class="cart-item__meta" style="color:rgba(201,168,76,0.6);font-size:9px;">✦ Ritual note included</div>` : ''}
          <div class="cart-item__meta">${item.sku ? 'SKU: ' + item.sku : ''}</div>
        </div>
        <div class="cart-item__price-col">
          <div class="cart-item__price">${MA.formatMoney(item.final_line_price)}</div>
          <div class="qty-ctrl" data-qty>
            <button class="qty-ctrl__btn" data-qty-dec data-key="${item.key}" aria-label="Decrease quantity">−</button>
            <span class="qty-ctrl__val">${item.quantity}</span>
            <button class="qty-ctrl__btn" data-qty-inc data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-item__remove" data-remove="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');
  }

  bindEvents() {
    MA.qsa('[data-qty-dec]', this.body).forEach(btn => {
      MA.on(btn, 'click', () => this.updateQty(btn.dataset.key, -1));
    });
    MA.qsa('[data-qty-inc]', this.body).forEach(btn => {
      MA.on(btn, 'click', () => this.updateQty(btn.dataset.key, 1));
    });
    MA.qsa('[data-remove]', this.body).forEach(btn => {
      MA.on(btn, 'click', () => this.removeItem(btn.dataset.remove));
    });
  }

  async updateQty(key, delta) {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const newQty = Math.max(0, item.quantity + delta);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      MA.emit('cart:updated');
    } catch (e) { console.error(e); }
  }

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      MA.emit('cart:updated');
    } catch (e) { console.error(e); }
  }
}

/* ─────────────────────────────────────────────
   STICKY ATC (Product Detail Page)
───────────────────────────────────────────── */
class StickyAtc {
  constructor() {
    this.atc = MA.qs('.sticky-atc');
    this.mainAtc = MA.qs('.pdp-atc-section');
    if (!this.atc || !this.mainAtc) return;
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        this.atc.style.display = e.isIntersecting ? 'none' : 'flex';
      });
    }, { threshold: 0.1 });
    this.observer.observe(this.mainAtc);
  }
}

/* ─────────────────────────────────────────────
   RITUAL NOTE TOGGLE
───────────────────────────────────────────── */
class RitualNoteToggle {
  constructor(el) {
    this.checkbox = el;
    this.target = MA.qs(el.dataset.noteTarget || '[data-ritual-note-field]');
    MA.on(el, 'change', () => {
      if (this.target) {
        this.target.style.display = el.checked ? 'block' : 'none';
        if (el.checked) MA.qs('textarea', this.target)?.focus();
      }
    });
  }
}

/* ─────────────────────────────────────────────
   SHIPPING OPTION SELECTOR
───────────────────────────────────────────── */
class ShippingSelector {
  constructor(el) {
    this.opts = MA.qsa('.ship-opt', el);
    this.totalEl = MA.qs('[data-shipping-total]');
    this.shippingEl = MA.qs('[data-shipping-val]');
    this.init();
  }

  init() {
    this.opts.forEach(opt => {
      MA.on(opt, 'click', () => {
        this.opts.forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        opt.querySelector('input[type=radio]').checked = true;
        const price = opt.dataset.price;
        const total = opt.dataset.total;
        if (this.shippingEl) {
          this.shippingEl.textContent = price === '0' ? 'Free' : MA.formatMoney(parseInt(price));
          this.shippingEl.className = 'order-line__val' + (price === '0' ? ' order-line--free' : '');
        }
        if (this.totalEl) this.totalEl.textContent = MA.formatMoney(parseInt(total));
        MA.emit('shipping:selected', { price, total });
      });
    });
  }
}

/* ─────────────────────────────────────────────
   PROMO CODE
───────────────────────────────────────────── */
class PromoCode {
  constructor(el) {
    this.container = el;
    this.input = MA.qs('[data-promo-input]', el);
    this.btn = MA.qs('[data-promo-btn]', el);
    this.msg = MA.qs('[data-promo-msg]', el);
    // Valid codes — in production these would be validated server-side via Shopify discount API
    this.valid = ['LOUNGE10', 'SOL10', 'NEWMOON', 'SOLALCHEMY'];
    MA.on(this.btn, 'click', () => this.apply());
    MA.on(this.input, 'keydown', e => { if (e.key === 'Enter') this.apply(); });
  }

  apply() {
    const code = this.input?.value?.trim()?.toUpperCase();
    if (!code) return;
    if (this.valid.includes(code)) {
      this.showMsg(`✓ ${code} applied — 10% off your order`, 'var(--sage-lt)');
      MA.emit('promo:applied', { code });
    } else {
      this.showMsg('Code not recognized. Try LOUNGE10', 'var(--red)');
    }
  }

  showMsg(text, color) {
    if (!this.msg) return;
    this.msg.textContent = text;
    this.msg.style.color = color;
    this.msg.style.display = 'block';
  }
}

/* ─────────────────────────────────────────────
   IMAGE GALLERY (PDP)
───────────────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.mainImg = MA.qs('[data-gallery-main]', el);
    this.thumbs = MA.qsa('[data-gallery-thumb]', el);
    this.init();
  }

  init() {
    this.thumbs.forEach(thumb => {
      MA.on(thumb, 'click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt || '';
        if (this.mainImg) {
          this.mainImg.src = src;
          this.mainImg.alt = alt;
        }
        this.thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }
}

/* ─────────────────────────────────────────────
   LIVE SCHEDULE (Lounge Page)
───────────────────────────────────────────── */
class LiveSchedule {
  constructor() {
    this.schedItems = MA.qsa('.sched-item');
    this.calDays = MA.qsa('.cal-day');
    this.highlightToday();
  }

  highlightToday() {
    const today = new Date().getDay(); // 0=Sun
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    this.calDays.forEach(day => {
      const nameEl = MA.qs('.cal-day-name', day);
      if (nameEl?.textContent.trim() === days[today]) {
        day.classList.add('today');
      }
    });
  }
}

/* ─────────────────────────────────────────────
   TICKER AUTO-DUPLICATE (for seamless loop)
───────────────────────────────────────────── */
class Ticker {
  constructor(el) {
    this.inner = MA.qs('.ticker__inner', el);
    if (!this.inner) return;
    // Duplicate content for seamless loop
    const clone = this.inner.innerHTML;
    this.inner.innerHTML += clone;
  }
}

/* ─────────────────────────────────────────────
   CHECKOUT PROGRESS
───────────────────────────────────────────── */
class CheckoutProgress {
  constructor() {
    this.steps = MA.qsa('.checkout-step');
    this.bar = MA.qs('.progress-bar__fill');
    this.stages = ['cart', 'info', 'shipping', 'payment', 'confirm'];
    this.current = 0;
  }

  goto(stageName) {
    const idx = this.stages.indexOf(stageName);
    if (idx < 0) return;
    this.current = idx;

    this.steps.forEach((step, i) => {
      step.classList.remove('is-active', 'is-done');
      const numEl = MA.qs('.checkout-step__num', step);
      if (i < idx) {
        step.classList.add('is-done');
        if (numEl) numEl.textContent = '✓';
      } else if (i === idx) {
        step.classList.add('is-active');
        if (numEl) numEl.textContent = i + 1;
      } else {
        if (numEl) numEl.textContent = i + 1;
      }
    });

    if (this.bar) {
      this.bar.style.width = ((idx + 1) / this.stages.length * 100) + '%';
    }

    MA.emit('checkout:stage', { stage: stageName, index: idx });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

/* ─────────────────────────────────────────────
   METAFIELD: QR VIDEO LINK
───────────────────────────────────────────── */
class QRVideoLink {
  constructor(el) {
    // el should have data-product-id and data-batch-id
    const productId = el.dataset.productId;
    const batchId = el.dataset.batchId;
    const videoUrl = el.dataset.videoUrl;

    if (videoUrl) {
      MA.on(el, 'click', () => window.open(videoUrl, '_blank'));
      el.style.cursor = 'pointer';
    }
  }
}

/* ─────────────────────────────────────────────
   INITIALISE ALL COMPONENTS
───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  // Header
  const header = new SiteHeader();

  // Cart drawer
  new CartDrawer();
  header.updateCartCount();

  // Accordions
  MA.qsa('.accordion').forEach(el => new Accordion(el));

  // Filter pills
  MA.qsa('[data-filter-bar]').forEach(el => new FilterPills(el));

  // Quantity controls
  MA.qsa('[data-qty-ctrl]').forEach(el => new QuantityControl(el));

  // Add to cart forms
  MA.qsa('[data-atc-form]').forEach(form => new AddToCart(form));

  // Sticky ATC
  new StickyAtc();

  // Ritual note toggles
  MA.qsa('[data-ritual-note-toggle]').forEach(el => new RitualNoteToggle(el));

  // Shipping selector
  const shipContainer = MA.qs('[data-ship-opts]');
  if (shipContainer) new ShippingSelector(shipContainer);

  // Promo code
  MA.qsa('[data-promo]').forEach(el => new PromoCode(el));

  // Product galleries
  MA.qsa('[data-gallery]').forEach(el => new ProductGallery(el));

  // Live schedule
  if (MA.qs('.lounge-page')) new LiveSchedule();

  // Ticker
  MA.qsa('.ticker').forEach(el => new Ticker(el));

  // Checkout progress
  const checkoutProgress = new CheckoutProgress();
  window.MA_Checkout = checkoutProgress; // expose for inline script use

  // QR video links
  MA.qsa('[data-qr-video]').forEach(el => new QRVideoLink(el));

  // Expose MA globally for Shopify sections
  window.MA = MA;
});
